import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import type { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  getGetConvertResultExpendituresQueryKey,
  useGetConvertResultExpenditureOptions,
  usePatchSettlementStatisticsExpenditure,
} from '@/entities/settlementstatisticsconverter/api/generated/api';
import type { ExpenditureRecordResponse } from '@/entities/settlementstatisticsconverter/api/generated/model';
import { buildSettlementCascaderOptions } from '@/entities/settlementstatisticsconverter/lib/buildSettlementCascaderOptions';
import {
  FundingTypeValue,
  fundingTypeSelectOptions,
} from '@/entities/settlementstatisticsconverter/lib/funding-type';
import {
  RecurringTypeValue,
  recurringTypeSelectOptions,
} from '@/entities/settlementstatisticsconverter/lib/recurring-type';
import { formatCodeNamePairs } from '@/shared/lib/format';
import {
  Button,
  CascaderField,
  Form,
  LabeledText,
  SelectField,
  Skeleton,
  Text,
  toast,
  useSkeletonMinDisplay,
} from '@/shared/ui';
import { SideMenuFormFooter } from '@/widgets/right-side-menu/ui/SideMenuFormFooter';
import { type ExpenditureEditFormValues, expenditureEditSchema } from './schema';

type ExpenditureEditFormProps = {
  /** 変換結果ID */
  convertResultId: string;
  /** 編集対象の歳出レコード */
  selectedExpenditure: ExpenditureRecordResponse;
  /** パネルを閉じる */
  onClose: () => void;
};

/**
 * 歳出コード振り結果 訂正フォーム
 *
 * 決算統計目的、決算統計性質、臨時経常区分、単独補助区分の4フィールドを編集するフォーム。
 * 離脱アラート（leavePageAlertCondition）を含む。
 */
export const ExpenditureEditForm: FC<ExpenditureEditFormProps> = ({
  convertResultId,
  selectedExpenditure,
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // 決算統計目的・性質オプション取得
  const { data: optionsData, isLoading: isOptionsLoading } = useGetConvertResultExpenditureOptions(
    convertResultId,
    { fields: ['expenditure_settlement_purpose', 'expenditure_settlement_nature'] },
  );

  // 決算統計目的のCascaderオプション
  const settlementPurposeOptions = buildSettlementCascaderOptions({
    settlements: optionsData?.expenditure_settlement_purposes,
  });

  // 決算統計性質のCascaderオプション
  const settlementNatureOptions = buildSettlementCascaderOptions({
    settlements: optionsData?.expenditure_settlement_natures,
  });

  // 初期値を設定（コードパス）
  const defaultSettlementPurposeCodePath = selectedExpenditure.settlement_purpose_value
    ? [
        selectedExpenditure.settlement_purpose_value.level1_code,
        selectedExpenditure.settlement_purpose_value.level2_code,
        selectedExpenditure.settlement_purpose_value.level3_code,
        selectedExpenditure.settlement_purpose_value.level4_code,
      ].filter(Boolean)
    : [];

  const defaultSettlementNatureCodePath = selectedExpenditure.settlement_nature_value
    ? [
        selectedExpenditure.settlement_nature_value.level1_code,
        selectedExpenditure.settlement_nature_value.level2_code,
        selectedExpenditure.settlement_nature_value.level3_code,
        selectedExpenditure.settlement_nature_value.level4_code,
      ].filter(Boolean)
    : [];

  const form = useForm<ExpenditureEditFormValues>({
    resolver: zodResolver(expenditureEditSchema),
    defaultValues: {
      settlement_purpose_code_path: defaultSettlementPurposeCodePath,
      settlement_nature_code_path: defaultSettlementNatureCodePath,
      recurring_type_value:
        (selectedExpenditure.recurring_type_value as RecurringTypeValue) ?? RecurringTypeValue.経常,
      funding_type_value:
        (selectedExpenditure.funding_type_value as FundingTypeValue) ?? FundingTypeValue.単独,
    },
  });

  const { mutate: patchExpenditure, isPending } = usePatchSettlementStatisticsExpenditure();

  const handleSubmit = (values: ExpenditureEditFormValues) => {
    // 決算統計目的: codePath → UUID
    const purposeCodePath = values.settlement_purpose_code_path;
    const settlementPurposeValueId = optionsData?.expenditure_settlement_purposes?.find(
      (opt) =>
        opt.level1_code === purposeCodePath[0] &&
        (purposeCodePath[1] ? opt.level2_code === purposeCodePath[1] : true) &&
        (purposeCodePath[2] ? opt.level3_code === purposeCodePath[2] : true) &&
        (purposeCodePath[3] ? opt.level4_code === purposeCodePath[3] : true),
    )?.id;

    // 決算統計性質: codePath → UUID
    const natureCodePath = values.settlement_nature_code_path;
    const settlementNatureValueId = optionsData?.expenditure_settlement_natures?.find(
      (opt) =>
        opt.level1_code === natureCodePath[0] &&
        (natureCodePath[1] ? opt.level2_code === natureCodePath[1] : true) &&
        (natureCodePath[2] ? opt.level3_code === natureCodePath[2] : true) &&
        (natureCodePath[3] ? opt.level4_code === natureCodePath[3] : true),
    )?.id;

    if (!settlementPurposeValueId || !settlementNatureValueId) {
      toast.error(t('common.messages.update-failed', { name: '歳出コード振り結果' }));
      return;
    }

    patchExpenditure(
      {
        convertResultId,
        expenditureId: selectedExpenditure.id,
        data: {
          settlement_purpose_value_id: settlementPurposeValueId,
          settlement_nature_value_id: settlementNatureValueId,
          recurring_type_value: values.recurring_type_value,
          funding_type_value: values.funding_type_value,
        },
      },
      {
        onSuccess: () => {
          toast.success(t('common.messages.update-success', { name: '歳出コード振り結果' }));
          queryClient.invalidateQueries({
            queryKey: getGetConvertResultExpendituresQueryKey(convertResultId),
          });
          onClose();
        },
        onError: () => {
          toast.error(t('common.messages.update-failed', { name: '歳出コード振り結果' }));
        },
      },
    );
  };

  const showSkeleton = useSkeletonMinDisplay(isOptionsLoading);
  if (showSkeleton) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <Form.Provider {...form}>
      <Form
        leavePageAlertCondition={{
          dirtyFields: form.formState.dirtyFields,
          isSubmitting: form.formState.isSubmitting,
          isSubmitSuccessful: form.formState.isSubmitSuccessful,
        }}
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex min-h-0 flex-1 flex-col"
      >
        {/* スクロール可能なフォーム領域 */}
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-4">
          {/* 行情報（読み取り専用） */}

          <LabeledText label={t('common.fields.accounting-type')}>
            {formatCodeNamePairs([
              [selectedExpenditure.accounting_type_code, selectedExpenditure.accounting_type_name],
            ])}
          </LabeledText>
          <LabeledText label={t('common.fields.expenditure-category')}>
            {formatCodeNamePairs([
              [selectedExpenditure.kan_code, selectedExpenditure.kan_name],
              [selectedExpenditure.ko_code, selectedExpenditure.ko_name],
              [selectedExpenditure.moku_code, selectedExpenditure.moku_name],
            ])}
          </LabeledText>
          <LabeledText label={t('settlement-statistics-converter.common-fields.project-category')}>
            {formatCodeNamePairs([
              [
                selectedExpenditure.project_category1_code,
                selectedExpenditure.project_category1_name,
              ],
              [
                selectedExpenditure.project_category2_code,
                selectedExpenditure.project_category2_name,
              ],
              [
                selectedExpenditure.project_category3_code,
                selectedExpenditure.project_category3_name,
              ],
            ])}
          </LabeledText>
          <LabeledText label={t('common.fields.project')}>
            {formatCodeNamePairs([
              [selectedExpenditure.project_code, selectedExpenditure.project_name],
            ])}
          </LabeledText>
          <LabeledText
            label={t('settlement-statistics-converter.expenditure.expenditure-subsection')}
          >
            {formatCodeNamePairs([
              [selectedExpenditure.setsu_code, selectedExpenditure.setsu_name],
              [selectedExpenditure.saisetsu_code, selectedExpenditure.saisetsu_name],
              [selectedExpenditure.saisaisetsu_code, selectedExpenditure.saisaisetsu_name],
            ])}
          </LabeledText>
          <LabeledText label={t('common.fields.description')}>
            {selectedExpenditure.description}
          </LabeledText>
          <LabeledText label={t('common.fields.amount')}>
            <Text viewMode="number">{selectedExpenditure.amount}</Text>
          </LabeledText>

          {/* 編集フィールド */}
          <CascaderField
            control={form.control}
            name="settlement_purpose_code_path"
            label={t('settlement-statistics-converter.common-fields.settlement-purpose')}
            options={settlementPurposeOptions}
            showSearch
            showAllClear
            onlyLeafSelectable
            required
          />

          <CascaderField
            control={form.control}
            name="settlement_nature_code_path"
            label={t('settlement-statistics-converter.common-fields.settlement-nature')}
            options={settlementNatureOptions}
            showSearch
            showAllClear
            onlyLeafSelectable
            required
          />

          <SelectField
            control={form.control}
            name="recurring_type_value"
            label={t('common.fields.recurring-type')}
            options={recurringTypeSelectOptions}
            required
          />

          <SelectField
            control={form.control}
            name="funding_type_value"
            label={t('settlement-statistics-converter.expenditure.funding-type')}
            options={fundingTypeSelectOptions}
            required
          />
        </div>

        <SideMenuFormFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onClose();
            }}
            disabled={isPending}
          >
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {t('common.actions.save')}
          </Button>
        </SideMenuFormFooter>
      </Form>
    </Form.Provider>
  );
};
